import type { ChangeEvent, KeyboardEvent } from 'react';

import { ImagePlusIcon, PaperclipIcon, SendHorizonalIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { messageFormProps } from '@/components/features/chat/layout/Chat';
import type { FileUploadActions } from '@/hooks/file-upload';

import { CHAT_MAX_FILE_SIZE, CHAT_MAX_MESSAGE_SIZE } from '@/components/features/chat/layout/Chat';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useChatStore } from '@/store/chat-store';

interface ChatConversationFooterProps {
  onSubmit: (props: messageFormProps) => void;
  fsUploadActions: FileUploadActions;
}

export default function ChatFooter(chatConversationFooterProps: ChatConversationFooterProps) {
  const { onSubmit, fsUploadActions } = chatConversationFooterProps;
  const [text, setText] = useState<string>('');
  const replyingTo = useChatStore((state) => state.replyingTo);
  const connectionStatus = useChatStore((state) => state.connectionStatus);
  const disableSubmition = connectionStatus === 'connecting' || connectionStatus === 'disconnected';

  const fileInput = fsUploadActions.createInputProps({
    'aria-label': 'Upload files',
    multiple: true,
    accept: '*/*',
    size: CHAT_MAX_FILE_SIZE,
    disabled: disableSubmition,
  });

  const mediaInput = fsUploadActions.createInputProps({
    'aria-label': 'Upload media files',
    multiple: true,
    size: CHAT_MAX_FILE_SIZE,
    disabled: disableSubmition,
    accept:
      'image/jpeg, image/png, image/gif, image/svg+xml, image/webp, audio/mpeg, audio/ogg, audio/3gp, audio/mp4, audio/webm, audio/flac, audio/object, video/mp4, video/webm, video/3gp, video/ogg, video/avi, video/mpeg',
  });

  const onCloseReply = () => {
    useChatStore.getState().setReplyingTo(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.shiftKey) {
      return event;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleOnSubmit();
    }
  };

  const onValueChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    if (text.trim().length > CHAT_MAX_MESSAGE_SIZE) {
      toast(`Wiadomość jest za długa,  ${text.trim().length}/2056 znaków`);
    }
    setText(text);
  };

  const handleOnSubmit = () => {
    if (!text) {
      return;
    }
    onSubmit({
      content: text.trim(),
      replyingTo: replyingTo,
    });

    setText('');
    onCloseReply();
  };

  return (
    <footer className={'border-border-primary bg-background relative space-y-2 border-t p-4'}>
      <input id={'media-upload'} ref={mediaInput.ref} className={'sr-only'} {...mediaInput.props} />
      <input id={'file-upload'} ref={fileInput.ref} className={'sr-only'} {...fileInput.props} />
      {replyingTo && (
        <div className={'flex gap-2'}>
          <span className={'text-text-tertiary shrink-0 text-sm'}>Odpowiedz:</span>
          <p className={'text-text-tertiary wrap-break-words line-clamp-3 w-full text-sm'}>{replyingTo.content}</p>
          <Button size={'sm'} variant={'ghost'} className={'h-8 min-h-8 w-8 min-w-8'} onClick={onCloseReply}>
            <XIcon />
          </Button>
        </div>
      )}
      <div className={'relative'}>
        <Textarea
          autoFocus
          onKeyDown={handleKeyDown}
          placeholder={'Wiadomość....'}
          value={text}
          onChange={onValueChange}
          className={
            'ring-offset-none active:!bg-transparent! field-sizing-content max-h-20 min-h-10 min-w-full resize-none rounded-none border-none bg-transparent! p-0 hover:bg-transparent! focus-visible:bg-transparent! focus-visible:outline-hidden focus-visible:outline-0 focus-visible:outline-offset-0 disabled:bg-transparent!'
          }
        />
      </div>
      <div className={'flex gap-2'}>
        <div className={'flex gap-0.5'}>
          <Button type={'button'} variant={'ghost'} onClick={() => mediaInput.openFileDialog()}>
            <ImagePlusIcon />
          </Button>
          <Button type={'button'} variant={'ghost'} onClick={() => fileInput.openFileDialog()}>
            <PaperclipIcon />
          </Button>
        </div>
        <Button className={'ml-auto'} variant={'secondary'} disabled={disableSubmition} onClick={handleOnSubmit}>
          Wyślij
          <SendHorizonalIcon className="transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    </footer>
  );
}
